import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo';
import { fetchCollection, optimizeImage } from '../lib/cms';
import VideoFacade from '../components/VideoFacade';
import CategoryBadge from '../components/CategoryBadge';
import HelpNotice from '../components/HelpNotice';

export default function PresseFilmPage() {
  useSeo({
    title: 'Presse & Film',
    description: 'Presse & Film hos Skatunderlup: Faglige videoer, analyser, webinarer og officielle pressemeddelelser om dansk skat og retssikkerhed.',
  });

  const [items, setItems] = useState([]);
  const [filterType, setFilterType] = useState('alle'); // 'alle', 'film', 'presse'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function loadMedia() {
      setLoading(true);
      try {
        const res = await fetchCollection('presse_film', {
          sort: 'publish_date',
          order: 'desc',
          limit: 30,
        });
        if (isMounted) {
          setItems(res.items || []);
          setLoading(false);
        }
      } catch (err) {
        console.warn('Failed to load media items', err);
        if (isMounted) setLoading(false);
      }
    }
    loadMedia();
    return () => { isMounted = false; };
  }, []);

  const filteredItems = items.filter((item) => {
    if (filterType === 'alle') return true;
    return item.media_type === filterType;
  });

  const featuredItem = filteredItems.find((i) => i.media_type === 'film') || filteredItems[0];
  const listItems = filteredItems.filter((i) => i.id !== featuredItem?.id);

  return (
    <div className="min-h-screen bg-white">
      {/* Section Masthead Header */}
      <div className="border-b border-[#010b27] bg-[#010b27] text-white py-10 px-4">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#fd7302]">
            <i className="fa-solid fa-clapperboard" aria-hidden="true" />
            <span>Redaktionel medieafdeling</span>
          </div>
          <h1 className="mt-1 font-serif text-3xl sm:text-4xl lg:text-5xl font-black text-white">
            Presse & Film
          </h1>
          <p className="mt-2 max-w-2xl text-sm sm:text-base text-slate-300">
            Faglige videoanalyser, tv-debatter, optagelser af skattetilsynets procedurer og officielle pressemeddelelser fra Skatunderlup.
          </p>

          {/* Type Filter Buttons */}
          <div className="mt-6 flex flex-wrap items-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => setFilterType('alle')}
              className={`rounded-sm px-4 py-2 font-bold uppercase tracking-wider transition-colors ${
                filterType === 'alle'
                  ? 'bg-[#fd7302] text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              Alt indhold ({items.length})
            </button>
            <button
              type="button"
              onClick={() => setFilterType('film')}
              className={`rounded-sm px-4 py-2 font-bold uppercase tracking-wider transition-colors ${
                filterType === 'film'
                  ? 'bg-[#fd7302] text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              <i className="fa-solid fa-play mr-1.5 text-[10px]" aria-hidden="true" />
              Film & Video ({items.filter((i) => i.media_type === 'film').length})
            </button>
            <button
              type="button"
              onClick={() => setFilterType('presse')}
              className={`rounded-sm px-4 py-2 font-bold uppercase tracking-wider transition-colors ${
                filterType === 'presse'
                  ? 'bg-[#fd7302] text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              <i className="fa-solid fa-bullhorn mr-1.5 text-[10px]" aria-hidden="true" />
              Pressemeddelelser ({items.filter((i) => i.media_type === 'presse').length})
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-10">
        {loading ? (
          <div className="py-20 text-center font-serif text-slate-500">
            Henter presse- og videoindhold...
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="rounded-md border border-slate-200 bg-slate-50/50 p-12 text-center">
            <p className="font-serif text-lg text-[#010b27]">
              Intet indhold fundet for den valgte kategori.
            </p>
          </div>
        ) : (
          <>
            {/* Featured Video / Main Story */}
            {featuredItem && (
              <div className="mb-12 pb-10 border-b border-slate-200">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                  <div className="lg:col-span-7">
                    {featuredItem.media_type === 'film' ? (
                      <VideoFacade
                        videoUrl={featuredItem.video_url}
                        coverImage={featuredItem.cover_image}
                        title={featuredItem.title}
                        duration={featuredItem.duration}
                      />
                    ) : (
                      <div className="overflow-hidden rounded-md border border-slate-200 bg-slate-50">
                        <img
                          src={optimizeImage(featuredItem.cover_image?.[0], { width: 900 })}
                          alt={featuredItem.title}
                          className="aspect-video w-full object-cover"
                        />
                      </div>
                    )}
                  </div>

                  <div className="lg:col-span-5 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="rounded-xs bg-[#fd7302] px-2 py-0.5 text-[10px] font-bold text-white uppercase tracking-wider">
                          {featuredItem.media_type === 'film' ? 'Videofokus' : 'Pressemeddelelse'}
                        </span>
                        <CategoryBadge audience={featuredItem.audience} size="xs" />
                      </div>

                      <Link
                        to={`/presse-og-film/${featuredItem.slug?._ || featuredItem.slug || ''}`}
                        className="block font-serif text-2xl sm:text-3xl font-black text-[#010b27] hover:text-[#fd7302] transition-colors leading-tight"
                      >
                        {featuredItem.title}
                      </Link>

                      <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">
                        {featuredItem.description}
                      </p>
                    </div>

                    <div className="mt-6 pt-4 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
                      <time dateTime={featuredItem.publish_date}>
                        {featuredItem.publish_date}
                      </time>
                      <Link
                        to={`/presse-og-film/${featuredItem.slug?._ || featuredItem.slug || ''}`}
                        className="font-bold text-[#fd7302] hover:underline"
                      >
                        Læs presseomtale →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* List of remaining media / press releases */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl font-bold text-[#010b27] pb-3 mb-6 border-b border-slate-200">
                Alle indslag & udgivelser
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {listItems.map((item) => {
                  const isFilm = item.media_type === 'film';
                  const slug = item.slug?._ || item.slug || '';
                  return (
                    <article
                      key={item.id}
                      className="group rounded-md border border-slate-200 bg-white p-4 flex flex-col justify-between hover:border-[#fd7302]/60 hover:shadow-sm transition-all shadow-xs"
                    >
                      <div>
                        {isFilm ? (
                          <div className="mb-3">
                            <VideoFacade
                              videoUrl={item.video_url}
                              coverImage={item.cover_image}
                              title={item.title}
                              duration={item.duration}
                            />
                          </div>
                        ) : (
                          <Link
                            to={`/presse-og-film/${slug}`}
                            className="block mb-3 overflow-hidden rounded-md border border-slate-200 bg-[#010b27]"
                          >
                            <img
                              src={optimizeImage(item.cover_image?.[0], { width: 600 })}
                              alt={item.title}
                              className="aspect-video w-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </Link>
                        )}

                        <div className="flex items-center gap-2 mb-2">
                          <span
                            className={`px-1.5 py-0.5 rounded-xs text-[10px] font-bold uppercase tracking-wider ${
                              isFilm
                                ? 'bg-[#010b27] text-white'
                                : 'bg-[#fff7ed] text-[#fd7302]'
                            }`}
                          >
                            {isFilm ? 'Video' : 'Presse'}
                          </span>
                          <CategoryBadge audience={item.audience} size="xs" />
                        </div>

                        <Link
                          to={`/presse-og-film/${slug}`}
                          className="block font-serif text-lg font-bold text-[#010b27] hover:text-[#fd7302] transition-colors leading-snug"
                        >
                          {item.title}
                        </Link>

                        <p className="mt-2 text-xs sm:text-sm text-slate-600 line-clamp-3 leading-relaxed">
                          {item.description}
                        </p>
                      </div>

                      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                        <time dateTime={item.publish_date}>{item.publish_date}</time>
                        <Link
                          to={`/presse-og-film/${slug}`}
                          className="font-bold text-[#fd7302] hover:underline"
                        >
                          Se detaljer →
                        </Link>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            {/* Press Office Info Card */}
            <div className="rounded-md border border-slate-200 bg-slate-50/70 p-6 sm:p-8 shadow-xs">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#fd7302]">
                    Pressekontakt
                  </span>
                  <h3 className="font-serif text-2xl font-bold text-[#010b27] mt-1">
                    Henvendelser fra journalister og medier
                  </h3>
                  <p className="mt-2 text-xs sm:text-sm text-slate-600 max-w-xl">
                    Ønsker du baggrundskommentarer, skattefaglig ekspertvurdering til en artikel eller medvirken i debatprogrammer?
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <a
                    href="mailto:mmkh@lexnovo.dk"
                    className="inline-flex items-center gap-2 rounded-sm bg-[#010b27] px-4 py-2.5 text-xs font-semibold text-white hover:bg-[#fd7302] transition-colors whitespace-nowrap"
                  >
                    <i className="fa-solid fa-envelope" aria-hidden="true" />
                    <span>mmkh@lexnovo.dk</span>
                  </a>
                  <a
                    href="tel:61444604"
                    className="inline-flex items-center gap-2 rounded-sm border border-[#010b27] bg-transparent px-4 py-2.5 text-xs font-semibold text-[#010b27] hover:bg-[#010b27] hover:text-white transition-colors whitespace-nowrap"
                  >
                    <i className="fa-solid fa-phone" aria-hidden="true" />
                    <span>61 44 46 04</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Bottom Help Notice */}
            <div className="mt-12">
              <HelpNotice variant="banner" />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
